import { Alert, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { DEFAULT_BLOOM_STRENGTH } from "../../render/renderConstants";
import type { ModelInfo } from "../model/modelInfo";
import type { ModelLook } from "../model/modelLook";
import { ACCEPTED_MODEL_TYPES, DEFAULT_LOOK } from "../viewerConstants";
import { DropHighlight } from "./DropHighlight/DropHighlight";
import { EmptyState } from "./EmptyState/EmptyState";
import { LoadError } from "./LoadError/LoadError";
import "./ModelViewer.css";
import { SettingsPanel } from "./SettingsPanel/SettingsPanel";
import { useModelViewport } from "./useModelViewport";

export function ModelViewer() {
  const theme = useTheme();
  const canvasHost = useRef<HTMLDivElement>(null);
  const viewport = useModelViewport(canvasHost, theme.palette.background.default);

  const [file, setFile] = useState<File | null>(null);
  const [info, setInfo] = useState<ModelInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rejectedDrop, setRejectedDrop] = useState(false);
  const [looksByFileName, setLooksByFileName] = useState<Record<string, ModelLook>>({});
  const [playingClip, setPlayingClip] = useState<string | null>(null);
  const [bloomStrength, setBloomStrength] = useState(DEFAULT_BLOOM_STRENGTH);

  const openModel = useCallback((model: File) => {
    setFile(model);
    setInfo(null);
    setLoadError(null);
    setRejectedDrop(false);
    setPlayingClip(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive, open: browseForFile } = useDropzone({
    accept: ACCEPTED_MODEL_TYPES,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: ([model]) => openModel(model),
    onDropRejected: () => setRejectedDrop(true)
  });

  // NOTE: keyed by file name so re-dropping a fresh blender export (of the same model, same filename) keeps its settings
  const look = (file && looksByFileName[file.name]) || DEFAULT_LOOK;
  const updateLook = (next: ModelLook) => {
    if (file) setLooksByFileName((looks) => ({ ...looks, [file.name]: next }));
  };

  useEffect(() => {
    if (!viewport || !file) return;
    let isCurrentFile = true;
    viewport.loadModel(file).then(
      (loaded) => isCurrentFile && loaded && setInfo(loaded),
      (error: unknown) => isCurrentFile && setLoadError(error instanceof Error ? error.message : String(error))
    );
    return () => {
      isCurrentFile = false;
    };
  }, [viewport, file]);

  useEffect(() => viewport?.setLook(look), [viewport, look]);
  useEffect(() => viewport?.playClip(playingClip), [viewport, playingClip]);
  useEffect(() => viewport?.setBloomStrength(bloomStrength), [viewport, bloomStrength]);

  const loadedModel = file && info && !loadError ? { fileName: file.name, info } : null;

  return (
    <div {...getRootProps({ className: "model-viewer" })} data-testid="model-viewer">
      <div className="model-viewer-viewport">
        <input {...getInputProps()} data-testid="viewer-file-input" />
        <div ref={canvasHost} className="model-viewer-canvas" />

        <div className="model-viewer-toolbar">
          {file && (
            <Button variant="contained" onClick={browseForFile} data-testid="viewer-open">
              Open another file
            </Button>
          )}
          {file && !info && !loadError && <Typography color="text.secondary">Loading {file.name}…</Typography>}
          {rejectedDrop && (
            <Alert severity="warning" data-testid="viewer-rejected">
              Only .glb and .gltf files are supported
            </Alert>
          )}
        </div>

        {!file && <EmptyState onBrowse={browseForFile} />}
        {file && loadError && <LoadError fileName={file.name} message={loadError} />}
        {isDragActive && <DropHighlight />}
      </div>

      <SettingsPanel
        model={loadedModel}
        look={look}
        onLookChange={updateLook}
        playingClip={playingClip}
        onPlayClip={setPlayingClip}
        bloomStrength={bloomStrength}
        onBloomStrengthChange={setBloomStrength}
      />
    </div>
  );
}
